import React from 'react';

function PipelineForecast({ forecast }) {
  // Enhanced null checking
  if (!forecast || typeof forecast !== 'object') return null;
  
  // Provide safe defaults for missing properties
  const safeData = {
    forecast_30_days: forecast.forecast_30_days ?? 0,
    forecast_60_days: forecast.forecast_60_days ?? 0,
    forecast_90_days: forecast.forecast_90_days ?? 0,
    confidence: forecast.confidence || 'low',
    key_factors: forecast.key_factors || [],
    recommendations: forecast.recommendations || []
  };

  // Helper function to format forecast numbers
  const formatForecast = (value) => {
    if (value === 0 || value === null || value === undefined) {
      return "0-2";
    }
    // Create a range around the forecast value
    const lower = Math.max(0, value - 1);
    const upper = value + 2;
    return `${lower}-${upper}`;
  };

  // Helper function to get confidence percentage
  const getConfidencePercentage = (globalConfidence, localValue) => {
    if (localValue === 0) return 25; // Low confidence for zero values
    
    switch(globalConfidence) {
      case 'high': return 85;
      case 'medium': return 65;
      case 'low': return 45;
      default: return 50;
    }
  };

  const cards = [
    { 
      period: "NEXT 30 DAYS", 
      range: formatForecast(safeData.forecast_30_days), 
      confidence: getConfidencePercentage(safeData.confidence, safeData.forecast_30_days),
      barHeight: "40%" // Shortest bar
    },
    { 
      period: "NEXT 60 DAYS", 
      range: formatForecast(safeData.forecast_60_days), 
      confidence: getConfidencePercentage(safeData.confidence, safeData.forecast_60_days),
      barHeight: "65%" // Medium bar
    },
    { 
      period: "NEXT 90 DAYS", 
      range: formatForecast(safeData.forecast_90_days), 
      confidence: getConfidencePercentage(safeData.confidence, safeData.forecast_90_days),
      barHeight: "85%" // Tallest bar
    }
  ];

  return (
    <div className="pipeline-forecast-section">
      {/* Header with Icon */}
      <div className="pipeline-forecast-header">
        <i className="fas fa-bullseye"></i>
        <h2>Pipeline Forecast</h2>
      </div>

      {/* Three Cards */}
      <div className="pipeline-forecast-cards">
        {cards.map((card, index) => (
          <div key={index} className="forecast-card">
            {/* Card Title */}
            <h3 className="forecast-period">{card.period}</h3>
            
            {/* Bar Graph Container */}
            <div className="forecast-bar-container">
              {/* Number Range Above Bar */}
              <div className="forecast-range">{card.range}</div>
              
              {/* Bar Graph */}
              <div className="forecast-bar-wrapper">
                <div 
                  className="forecast-bar" 
                  style={{ height: card.barHeight }}
                ></div>
              </div>
            </div>
            
            {/* Confidence Below Bar */}
            <div className="forecast-confidence">{card.confidence}% confidence</div>
          </div>
        ))}
      </div>
      
      {/* Key Factors and Recommendations */}
      {(safeData.key_factors.length > 0) || (safeData.recommendations.length > 0) ? (
        <div className="forecast-insights">
          <div className="insights-grid">
            {safeData.key_factors.length > 0 && (
              <div className="insight-card key-factors-card">
                <div className="insight-header">
                  <i className="fas fa-lightbulb insight-icon"></i>
                  <h4>Key Factors</h4>
                </div>
                <ul className="insight-list">
                  {safeData.key_factors.map((factor, index) => (
                    <li key={index} className="insight-item">
                      <i className="fas fa-info-circle item-icon"></i>
                      <span>{factor}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {safeData.recommendations.length > 0 && (
              <div className="insight-card recommendations-card">
                <div className="insight-header">
                  <i className="fas fa-list-check insight-icon"></i>
                  <h4>Recommendations</h4>
                </div>
                <ul className="insight-list">
                  {safeData.recommendations.map((rec, index) => (
                    <li key={index} className="insight-item">
                      <i className="fas fa-arrow-right item-icon"></i>
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PipelineForecast;